import React, { useContext, useEffect, useState } from "react";
import { Tab, Tabs, TabList, TabPanel } from "react-tabs";
import { useSearchParams } from "react-router-dom";
import SideBar from "components/common/SideBar/SideBarNew";
import Header from "components/common/Header/Header";
import Breadcrumb from "components/common/Breadcrumb";
import CircularLoader from "components/common/CircularLoader/CircularLoader";
import { LoginContext } from "helpers/LoginContext";
import {
  SETTINGS_BREADCRUMB_PAGES,
  SP_SETTING_BREADCRUMB,
} from "Constants/BreadcrumbConstant";
import useServiceAgreementTemplate from "./hooks/useServiceAgreementTemplate";
import OverviewTab from "./tabs/OverviewTab";
import PdfTemplateTab from "./tabs/PdfTemplateTab";

const TAB_OVERVIEW = 0;
const TAB_PDF = 1;

const ServiceAgreementSetting = () => {
  const { sideBarPadding, isUserGracePeriodOver } = useContext(LoginContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const [tabIndex, setTabIndex] = useState(
    searchParams.get("tab") === "pdf-template" ? TAB_PDF : TAB_OVERVIEW
  );
  const [switchingToDefault, setSwitchingToDefault] = useState(false);

  const {
    loading,
    saving,
    clearing,
    previewing,
    template,
    starter,
    forms,
    selectedFormTemplateId,
    setSelectedFormTemplateId,
    availableFields,
    loadTemplate,
    loadAvailableFields,
    saveTemplate,
    switchTemplateMode,
    clearTemplate,
    previewTemplate,
  } = useServiceAgreementTemplate();

  useEffect(() => {
    loadTemplate();
    loadAvailableFields();
  }, [loadTemplate, loadAvailableFields]);

  const handleTabSelect = (index) => {
    setTabIndex(index);
    if (index === TAB_PDF) {
      setSearchParams({ tab: "pdf-template" });
    } else {
      setSearchParams({});
    }
  };

  const handleFormChange = async (formId) => {
    const nextId = formId != null ? Number(formId) : null;
    setSelectedFormTemplateId(nextId);
    // Silent reload keeps the Customise tab mounted (no full-page loader flash).
    await loadTemplate(nextId, { silent: true });
    await loadAvailableFields(nextId);
  };

  const handleClear = async () => {
    if (
      !window.confirm(
        "Switch to the default PDF for this form? Your custom draft will be kept so you can switch back later."
      )
    ) {
      return;
    }
    setSwitchingToDefault(true);
    try {
      const ok = await clearTemplate(selectedFormTemplateId);
      if (ok) {
        await loadTemplate(selectedFormTemplateId, { silent: true });
        await loadAvailableFields(selectedFormTemplateId);
        handleTabSelect(TAB_OVERVIEW);
      }
    } finally {
      setSwitchingToDefault(false);
    }
  };

  const handleSwitchMode = async (templateMode) => {
    const saved = await switchTemplateMode(
      selectedFormTemplateId,
      templateMode
    );
    if (saved) {
      await loadTemplate(selectedFormTemplateId, { silent: true });
      await loadAvailableFields(selectedFormTemplateId);
    }
  };

  const handleConfigureForm = async (formId) => {
    // Switch tab immediately so Overview never reappears after the loader.
    handleTabSelect(TAB_PDF);
    await handleFormChange(formId);
  };

  const selectedForm =
    forms.find((f) => Number(f.id) === Number(selectedFormTemplateId)) ||
    null;
  const selectedTemplateMode =
    template?.template_mode ||
    selectedForm?.template_mode ||
    "default";
  const selectedFormHasCustom = Boolean(
    template?.has_custom ?? selectedForm?.has_custom
  );

  return (
    <div className="min-h-screen bg-gray-100">
      <SideBar />
      <div
        className={`${
          sideBarPadding ? "lg:pl-[4rem] duration-300" : "lg:pl-64 duration-300"
        } flex flex-col`}
      >
        <Header
          title="Service Agreement Settings"
          breadcrumb={
            <Breadcrumb
              pages={SP_SETTING_BREADCRUMB(
                SETTINGS_BREADCRUMB_PAGES.SERVICE_AGREEMENT_SETTING
              )}
            />
          }
        />
        <section className="w-full sm:px-6 lg:px-8 mt-3 pb-8">
          <div className="shadow sm:rounded-md sm:overflow-hidden p-4 bg-white">
            {loading || switchingToDefault ? (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <CircularLoader />
                {switchingToDefault ? (
                  <p className="text-sm text-gray-600">
                    Switching to default PDF…
                  </p>
                ) : null}
              </div>
            ) : (
              <Tabs selectedIndex={tabIndex} onSelect={handleTabSelect}>
                <TabList className="flex space-x-4 border-b mb-6">
                  <Tab className="text-gray-500 focus:border-b-2 focus:border-blue-dark focus:text-blue-dark cursor-pointer pb-3 focus:outline-0">
                    Overview
                  </Tab>
                  <Tab className="text-gray-500 focus:border-b-2 focus:border-blue-dark focus:text-blue-dark cursor-pointer pb-3 focus:outline-0">
                    Customise agreement
                  </Tab>
                </TabList>

                <TabPanel>
                  <OverviewTab
                    forms={forms}
                    onConfigureForm={handleConfigureForm}
                  />
                </TabPanel>

                <TabPanel>
                  <PdfTemplateTab
                    template={template}
                    starter={starter}
                    forms={forms}
                    selectedFormTemplateId={selectedFormTemplateId}
                    templateMode={selectedTemplateMode}
                    hasCustom={selectedFormHasCustom}
                    availableFields={availableFields}
                    saving={saving}
                    previewing={previewing}
                    clearing={clearing}
                    isUserGracePeriodOver={isUserGracePeriodOver}
                    onFormChange={handleFormChange}
                    onSwitchMode={handleSwitchMode}
                    onSave={async (body, isUpdate) => {
                      await saveTemplate(body, isUpdate);
                      await loadTemplate(body?.sp_form_template_id, {
                        silent: true,
                      });
                      await loadAvailableFields(body?.sp_form_template_id);
                    }}
                    onPreview={previewTemplate}
                    onClear={handleClear}
                  />
                </TabPanel>
              </Tabs>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default ServiceAgreementSetting;
